import { useStore } from 'zustand';
import { createStore } from 'zustand/vanilla';

/**
 * Chat-session selection state for the chat-history list.
 *
 * Lives in a module-level zustand store — deliberately outside the
 * masonry engine's state controller. Masonry re-initializes node
 * state on every async refresh (pagination) via `setInitialNodeState`,
 * which replaces node state wholesale; anything attached there would
 * be clobbered on each page change. A standalone store escapes that
 * lifecycle while still being observable.
 *
 * Scope assumption: at most one chat-session list is mounted at a
 * time. The list renderer registers a mount token via
 * `registerChatListMount`; the store only clears when the *last*
 * token unregisters so overlapping mount/unmount windows (e.g. route
 * transitions, Suspense) don't wipe state prematurely. A dev-only
 * warning fires if more than one list is mounted concurrently — if
 * that's ever a supported scenario, this store needs to be keyed by
 * list node id again (see git history for the keyed version).
 */
type ChatSelectionState = {
  selectedSessionId: string | null;
};

const initialState: ChatSelectionState = {
  selectedSessionId: null,
};

const store = createStore<ChatSelectionState>(() => ({ ...initialState }));

/**
 * Reactive subscription to the currently selected sessionId. Uses a
 * scalar selector, so subscribers re-render only when the ID's value
 * changes (not when the state object is merely re-emitted).
 */
export const useSelectedSessionId = (): string | null =>
  useStore(store, (s) => s.selectedSessionId);

/**
 * Imperative setter. Does not subscribe — safe to call from event
 * handlers and effects without adding a React dependency.
 */
export const selectChatSession = (sessionId: string): void => {
  store.setState({ selectedSessionId: sessionId });
};

/**
 * Imperative reset. Safe to call when already cleared.
 *
 * Prefer `registerChatListMount` from the list renderer for unmount
 * cleanup — it handles concurrent-mount windows correctly. Call this
 * directly only for test setup or an explicit in-app deselect.
 */
export const clearChatSelection = (): void => {
  store.setState({ ...initialState });
};

const mountedInstances = new Set<symbol>();

/**
 * Registers a chat-session-list mount. Returns an unregister function
 * that must be called on unmount.
 *
 * Instead of clearing on every unmount (which races during overlap
 * windows like route transitions), selection is cleared only when
 * the *last* mount unregisters. This keeps the singleton honest
 * under React 18 concurrent rendering, Suspense, and route-animation
 * patterns where a new instance can mount before the previous one
 * unmounts.
 *
 * Emits a dev-only error on concurrent mounts — the store is a
 * singleton and cannot correctly represent independent selections
 * for multiple lists.
 */
export const registerChatListMount = (): (() => void) => {
  const token = Symbol('chat-list-mount');
  mountedInstances.add(token);

  if (process.env.NODE_ENV !== 'production' && mountedInstances.size > 1) {
    // eslint-disable-next-line no-console
    console.error(
      '[chat-selection-store] More than one chat-session list is mounted concurrently. Selection state is a module-level singleton — selections will leak between instances. If you need simultaneous lists, key this store by list node id (see git history).',
    );
  }

  return () => {
    mountedInstances.delete(token);
    if (mountedInstances.size === 0) {
      store.setState({ ...initialState });
    }
  };
};
